/**
 * RouteCheckpointManager - Manage checkpoints (sectors) for a patrol route
 * P0 Patrols v2: Sector-based stop management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  MapPin,
  Plus,
  Trash2,
  GripVertical,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Building2,
  Layers
} from 'lucide-react';

export default function RouteCheckpointManager({ 
  open, 
  onOpenChange, 
  route, 
  onUpdate 
}) {
  const { api } = useAuth();
  
  const [stops, setStops] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [deletingStopId, setDeletingStopId] = useState(null);

  // Fetch route stops
  const fetchStops = useCallback(async () => {
    if (!route?.id) return;
    
    try {
      setLoading(true);
      const res = await api.get(`/patrols/routes/${route.id}/stops`);
      setStops(res.data.stops || []);
    } catch (error) {
      console.error('Error fetching stops:', error);
      toast.error('Failed to load checkpoints');
    } finally {
      setLoading(false);
    }
  }, [api, route?.id]);

  // Fetch locations for the route's site
  const fetchLocations = useCallback(async () => {
    if (!route?.site_id) return;
    
    try {
      // Correct endpoint: /sites/{site_id}/locations
      const res = await api.get(`/sites/${route.site_id}/locations`);
      setLocations(res.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Don't show error toast as this might just mean no locations exist
    }
  }, [api, route?.site_id]);

  // Fetch sectors for selected location
  const fetchSectors = useCallback(async () => {
    if (!selectedLocation) {
      setSectors([]);
      return;
    }
    
    try {
      const res = await api.get(`/locations/${selectedLocation}/sectors`);
      setSectors(res.data || []);
    } catch (error) {
      console.error('Error fetching sectors:', error);
    }
  }, [api, selectedLocation]);

  useEffect(() => {
    if (open && route?.id) {
      fetchStops();
      fetchLocations();
    }
  }, [open, route?.id, fetchStops, fetchLocations]);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // Add selected sectors as stops
  const handleAddSectors = async () => {
    if (selectedSectors.length === 0) {
      toast.error('Select at least one sector to add');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/patrols/routes/${route.id}/stops`, {
        sector_ids: selectedSectors,
        mode: 'append'
      });
      toast.success(`Added ${selectedSectors.length} checkpoint(s)`);
      setSelectedSectors([]);
      fetchStops();
      onUpdate?.();
    } catch (error) {
      console.error('Error adding stops:', error);
      toast.error(error.response?.data?.detail || 'Failed to add checkpoints');
    } finally {
      setSaving(false);
    }
  };

  // Add all sectors from location
  const handleAddAllFromLocation = async () => {
    if (sectors.length === 0) {
      toast.error('No sectors in this location');
      return;
    }
    
    const sectorIds = sectors.map(s => s.id);
    setSaving(true);
    try {
      await api.post(`/patrols/routes/${route.id}/stops`, {
        sector_ids: sectorIds,
        mode: 'append'
      });
      toast.success(`Added ${sectors.length} checkpoint(s) from ${locations.find(l => l.id === selectedLocation)?.name}`);
      fetchStops();
      onUpdate?.();
    } catch (error) {
      console.error('Error adding stops:', error);
      toast.error(error.response?.data?.detail || 'Failed to add checkpoints');
    } finally {
      setSaving(false);
    }
  };

  // Remove a stop
  const handleRemoveStop = async (stopId) => {
    setDeletingStopId(stopId);
    try {
      await api.delete(`/patrols/routes/${route.id}/stops/${stopId}`);
      toast.success('Checkpoint removed');
      fetchStops();
      onUpdate?.();
    } catch (error) {
      console.error('Error removing stop:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove checkpoint');
    } finally {
      setDeletingStopId(null);
    }
  };

  // Toggle sector selection
  const toggleSectorSelection = (sectorId) => {
    setSelectedSectors(prev => 
      prev.includes(sectorId) 
        ? prev.filter(id => id !== sectorId)
        : [...prev, sectorId]
    );
  };

  // Check if sector is already in route
  const isSectorInRoute = (sectorId) => {
    return stops.some(s => s.sector_id === sectorId);
  };

  if (!route) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-400" />
            Checkpoints: {route.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Add sectors as checkpoints for this patrol route. Site: {route.site_name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Current Stops List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300 text-sm font-medium">
                Current Checkpoints ({stops.length})
              </Label>
              {stops.length === 0 && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  No checkpoints - route is DRAFT
                </Badge>
              )}
            </div>
            
            {loading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" />
              </div>
            ) : stops.length === 0 ? (
              <div className="p-4 bg-[#09090b] rounded border border-[#3f3f46] text-center">
                <MapPin className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                <p className="text-sm text-zinc-500">No checkpoints configured yet</p>
                <p className="text-xs text-zinc-600 mt-1">Add sectors from locations below</p>
              </div>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto bg-[#09090b] rounded border border-[#3f3f46] p-2">
                {stops.map((stop, index) => (
                  <div 
                    key={stop.id}
                    className="flex items-center gap-3 p-2 bg-[#18181b] rounded hover:bg-[#27272a] transition-colors"
                    data-testid={`stop-item-${stop.id}`}
                  >
                    <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab" />
                    <span className="w-6 h-6 rounded bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-mono">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-200 truncate">{stop.sector_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{stop.location_name}</p>
                    </div>
                    {stop.gps_lat && stop.gps_lng && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">GPS</Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleRemoveStop(stop.id)}
                      disabled={deletingStopId === stop.id}
                    >
                      {deletingStopId === stop.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Sectors Section */}
          <div className="space-y-3 pt-2 border-t border-[#3f3f46]">
            <Label className="text-zinc-300 text-sm font-medium flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Checkpoints from Sectors
            </Label>
            
            {/* Location Selector */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Select Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="location-select">
                  <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                  <SelectValue placeholder="Choose a location..." />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  {locations.length === 0 ? (
                    <div className="p-2 text-sm text-zinc-500 text-center">No locations for this site</div>
                  ) : (
                    locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.sectors_count || 0} sectors)
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Sectors from selected location */}
            {selectedLocation && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400 text-xs">Available Sectors</Label>
                  {sectors.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-blue-400 hover:text-blue-300"
                      onClick={handleAddAllFromLocation}
                      disabled={saving}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add All ({sectors.length})
                    </Button>
                  )}
                </div>
                
                {sectors.length === 0 ? (
                  <div className="p-3 bg-[#09090b] rounded border border-[#3f3f46] text-center">
                    <Layers className="w-6 h-6 mx-auto text-zinc-600 mb-1" />
                    <p className="text-xs text-zinc-500">No sectors in this location</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-[#09090b] rounded border border-[#3f3f46]">
                    {sectors.map(sector => {
                      const inRoute = isSectorInRoute(sector.id);
                      const selected = selectedSectors.includes(sector.id);
                      
                      return (
                        <div
                          key={sector.id}
                          onClick={() => !inRoute && toggleSectorSelection(sector.id)}
                          className={`p-2 rounded border cursor-pointer transition-all ${
                            inRoute 
                              ? 'bg-zinc-800/50 border-zinc-700 opacity-50 cursor-not-allowed'
                              : selected
                                ? 'bg-blue-500/20 border-blue-500/50'
                                : 'bg-[#18181b] border-[#3f3f46] hover:border-zinc-500'
                          }`}
                          data-testid={`sector-option-${sector.id}`}
                        >
                          <div className="flex items-center gap-2">
                            {inRoute ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            ) : selected ? (
                              <CheckCircle2 className="w-4 h-4 text-blue-400" />
                            ) : (
                              <div className="w-4 h-4 rounded border border-zinc-600" />
                            )}
                            <span className="text-sm text-zinc-200 truncate">{sector.name}</span>
                          </div>
                          {inRoute && (
                            <span className="text-xs text-zinc-500 ml-6">Already added</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Selected Button */}
                {selectedSectors.length > 0 && (
                  <Button
                    className="w-full bg-blue-500 hover:bg-blue-600"
                    onClick={handleAddSectors}
                    disabled={saving}
                    data-testid="add-selected-sectors-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Selected ({selectedSectors.length})
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t border-[#3f3f46] pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-600">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
